import React, { ReactNode, useMemo } from 'react';
import { PricePlanCardList2 } from '@singtel-web/widgets';
import { Spacing, Grid } from '@dls/web';
import { parsePlanDescription } from '@common-modules/product-catalogue';

import { trans as t } from '../../helpers/localisation';

import formatPrice from '../../helpers/formatPrice';
import { isValidArrayWithData } from '../../helpers/common';
import { getPlanTagConfig } from '../../helpers/productDetails';

interface PricePlanCardsProps {
  plansData: {
    planName: string;
    ribbon?: string;
    monthlyCharges?: string;
    discountedPrice?: string;
    $discountedPrice?: string;
    planDescSubLiner?: string;
    tags?: string[];
    [key: string]: unknown;
  }[];
  networkSpeedMap: string;
  isCisPlanCatalogue: boolean;
  onPlanClick: (plan: PricePlanCardsProps['plansData'][number]) => void;
}

const PricePlanCards = ({
  plansData = [],
  networkSpeedMap,
  isCisPlanCatalogue,
  onPlanClick
}: PricePlanCardsProps): ReactNode => {
  const mapCard = (plan: PricePlanCardsProps['plansData'][number]) => {
    const {
      planName,
      ribbon,
      monthlyCharges,
      discountedPrice,
      $discountedPrice,
      planDescSubLiner,
      tags,
      planExtraLiner
    } = plan;
    const parsedActualPrice = parseFloat(monthlyCharges || '');
    const parsedDiscountedPrice = parseFloat(
      (isCisPlanCatalogue ? $discountedPrice : discountedPrice) || ''
    );
    const mainPrice =
      parsedDiscountedPrice >= 0
        ? `${formatPrice(parsedDiscountedPrice)}/mth`
        : `${formatPrice(parsedActualPrice)}/mth`;

    const hasTags = isValidArrayWithData(tags);
    const tagConfig = hasTags
      ? getPlanTagConfig(tags || [], networkSpeedMap)
      : null;

    const {
      content1Sections,
      content2Sections,
      content3Label,
      content1: legacyContent1,
      content2: legacyContent2,
      content3: content3Bullets = [],
      imageList = []
    } = parsePlanDescription(planDescSubLiner);

    const typedContent1Sections = (content1Sections || []) as Array<{
      label?: string;
      items?: unknown[];
    }>;
    const typedContent2Sections = (content2Sections || []) as Array<{
      label?: string;
      items?: unknown[];
    }>;

    const mappedContent1 = isValidArrayWithData(typedContent1Sections)
      ? typedContent1Sections.flatMap(section =>
          isValidArrayWithData(section.items)
            ? [
                {
                  title: section.label,
                  titleTextType: 'smallBody',
                  items: section.items,
                  divider: true
                }
              ]
            : []
        )
      : isValidArrayWithData(legacyContent1)
      ? [{ items: legacyContent1, divider: true }]
      : [];

    const mappedContent2 = isValidArrayWithData(typedContent2Sections)
      ? typedContent2Sections.flatMap(section =>
          isValidArrayWithData(section.items)
            ? [
                {
                  title: section.label,
                  titleTextType: 'smallBody',
                  bullets: section.items
                }
              ]
            : []
        )
      : isValidArrayWithData(legacyContent2)
      ? [
          {
            title: t('COMES_WITH'),
            titleTextType: 'smallBody',
            bullets: legacyContent2
          }
        ]
      : [];

    const mappedContent3 = content3Label
      ? {
          title: content3Label,
          titleTextType: 'smallBody',
          bullets: content3Bullets,
          imageList: imageList
        }
      : isValidArrayWithData(content3Bullets)
      ? {
          title: t('PROMOTIONS'),
          titleTextType: 'smallBody',
          bullets: content3Bullets,
          imageList: imageList
        }
      : {};

    return {
      bookmarkTag: ribbon
        ? { text: ribbon, color: '#fff', backgroundColor: '#0044CC' }
        : null,
      title: { text: planName, textType: 'sectionTitle' },
      tag: tagConfig
        ? {
            text: tagConfig.tagText,
            color: '#fff',
            backgroundColor: tagConfig.tagBgColor
          }
        : null,
      content1: mappedContent1,
      content2: mappedContent2,
      content3: mappedContent3,
      footerPricing: {
        divider: true,
        items: [
          {
            subtitle: planExtraLiner || '',
            title: mainPrice,
            ...(parsedDiscountedPrice < parsedActualPrice && {
              strikethroughTitle: formatPrice(parsedActualPrice)
            })
          }
        ]
      },
      cta: [
        {
          text: 'Get',
          type: 'secondary',
          onClick: () => onPlanClick(plan)
        }
      ]
    };
  };

  const mappedCards = plansData.map(mapCard);

  const countSections = (slot: string) =>
    Math.max(
      0,
      ...mappedCards.map(card => {
        const val = (card as Record<string, unknown>)[slot];
        return Array.isArray(val) ? val.length : 0;
      })
    );

  const hasContent3 = mappedCards.some(card => {
    const val = (card as Record<string, unknown>).content3;
    return (
      val &&
      typeof val === 'object' &&
      Object.keys(val as Record<string, unknown>).length > 0
    );
  });

  const slotOrder = [
    'title',
    'content4', // footerPricing + cta
    ...Array.from({ length: countSections('content1') }, () => 'content1'),
    ...Array.from({ length: countSections('content2') }, () => 'content2'),
    ...(hasContent3 ? ['content3'] : [])
  ];

  const cardList2Props = {
    numberOfCardPerRow: 3,
    slotOrder,
    tagPosition: 'top',
    cards: mappedCards
  };

  const cardsRenderKey = useMemo(
    () =>
      plansData
        .map(plan =>
          [
            plan.planName,
            String(plan.planId ?? ''),
            String(plan.groupName ?? ''),
            String(plan.planDescSubLiner ?? '')
          ].join('::')
        )
        .join('|'),
    [plansData]
  );

  return (
    <Spacing leftRight={0.25}>
      <Grid>
        <PricePlanCardList2 key={cardsRenderKey} {...cardList2Props} />
      </Grid>
    </Spacing>
  );
};

export default PricePlanCards;
